import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { getAPIBaseURL } from "../services/helpers"
import { getData, postData } from "../services/request"
import LoadingStatValue, { LoadingScoreValue } from "../components/LoadingStatValue"
import { applicationStatusLabel } from "../utils/employmeLabels"

const EmployerApplicantDetails = () => {
  const { jobId, applicantId } = useParams()
  const [application, setApplication] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [genMsg, setGenMsg] = useState("")
  const [score, setScore] = useState("")
  const [comment, setComment] = useState("")
  const [scoring, setScoring] = useState(false)
  const [messageBody, setMessageBody] = useState("")
  const [messageType, setMessageType] = useState("GENERIC")
  const [advice, setAdvice] = useState("")
  const [messaging, setMessaging] = useState(false)
  const [notesExpanded, setNotesExpanded] = useState(false)
  const [aiMessageLoading, setAiMessageLoading] = useState(false)
  const [templateLoading, setTemplateLoading] = useState(false)

  const load = async (silent = false) => {
    if (!silent) {
      setLoading(true)
      setError("")
    }
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${jobId}/applications/${applicantId}`
    const res = await getData(url, {}, {})
    if (!silent) setLoading(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        const data = res.result.data?.data || null
        setApplication(data)
        if (typeof data?.score === "number") {
          setScore(String(data.score))
        }
      } else if (!silent) {
        setError(res.result.data?.message || "Could not load application")
      }
    } else if (!silent) {
      setError(res.result?.data?.message || "Network error")
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, applicantId])

  useEffect(() => {
    const status = (application?.aiAnalysisStatus || "PENDING").toString().toUpperCase()
    if (!["PENDING", "IN_PROGRESS", "PARTIAL"].includes(status)) return undefined
    const timer = setInterval(() => {
      load(true)
    }, 5000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application?.aiAnalysisStatus, jobId, applicantId])

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const handleScore = async () => {
    setGenMsg("")
    setError("")
    const numericScore = Number(score)
    if (Number.isNaN(numericScore) || numericScore < 0 || numericScore > 100) {
      setError("Score must be between 0 and 100")
      return
    }
    setScoring(true)
    const url = `${getAPIBaseURL()}/employme/employer/applications/${applicantId}/score`
    const headers = { "Content-Type": "application/x-www-form-urlencoded" }
    const params = new URLSearchParams()
    params.append("score", String(numericScore))
    if (comment) params.append("comment", comment)
    const res = await postData(url, headers, params.toString())
    setScoring(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        setGenMsg(res.result.data?.message || "Score saved")
        load()
      } else {
        setError(res.result.data?.message || "Could not save score")
      }
    } else {
      setError(res.result?.data?.message || "Network error")
    }
  }

  const handleMessage = async () => {
    setGenMsg("")
    setError("")
    if (!messageBody.trim() && (messageType === "GENERIC")) {
      setError("Message cannot be empty")
      return
    }
    setMessaging(true)
    const url = `${getAPIBaseURL()}/employme/employer/applications/${applicantId}/messages`
    const headers = { "Content-Type": "application/x-www-form-urlencoded" }
    const params = new URLSearchParams()
    if (messageBody.trim()) {
      params.append("body", messageBody)
    }
    if (messageType && messageType !== "GENERIC") {
      params.append("messageType", messageType)
    }
    if (advice && advice.trim()) {
      params.append("advice", advice.trim())
    }
    const res = await postData(url, headers, params.toString())
    setMessaging(false)
    if (res.error === "") {
      if (res.result.status === 200 || res.result.status === 201) {
        setGenMsg(res.result.data?.message || "Message sent")
        setMessageBody("")
        setAdvice("")
        await load()
      } else {
        setError(res.result.data?.message || "Could not send message")
      }
    } else {
      setError(res.result?.data?.message || "Network error")
    }
  }

  const handleLoadTemplate = async () => {
    if (!applicantId) return
    if (messageType !== "ACCEPT" && messageType !== "REJECT") return
    setGenMsg("")
    setError("")
    setTemplateLoading(true)
    try {
      const url = `${getAPIBaseURL()}/employme/employer/applications/${applicantId}/message-template`
      const headers = { "Content-Type": "application/json" }
      const payload = { messageType, advice: advice || "" }
      const res = await postData(url, headers, JSON.stringify(payload))
      if (res.error === "" && res.result?.status === 200) {
        const data = res.result.data?.data || {}
        if (data.body) {
          setMessageBody(data.body)
          setGenMsg("Template loaded. Review and edit before sending.")
        } else {
          setError("Template is empty or unavailable.")
        }
      } else {
        setError(res.result?.data?.data || res.result?.data?.message || "Could not load template")
      }
    } finally {
      setTemplateLoading(false)
    }
  }

  useEffect(() => {
    // Auto-load template when switching to accept/reject if the textarea is empty.
    if (messageType === "ACCEPT" || messageType === "REJECT") {
      if (!messageBody.trim()) {
        handleLoadTemplate()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageType])

  const handleAiMessage = async () => {
    if (!messageType || messageType === "GENERIC") {
      setError("Choose Accept or Reject to generate a templated message.")
      return
    }
    setGenMsg("")
    setError("")
    setAiMessageLoading(true)
    try {
      const url = `${getAPIBaseURL()}/employme/employer/applications/${applicantId}/ai-messages`
      const axiosModule = await import("axios")
      const axios = axiosModule.default || axiosModule
      const res = await axios.post(
        url,
        {
          type:
            messageType === "ACCEPT"
              ? "INVITE_NEXT_STAGE"
              : messageType === "REJECT"
              ? "REJECTION"
              : messageType,
          extraContext: advice || ""
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true
        }
      )
      if (res.status === 200) {
        const data = res.data?.data || {}
        if (data.body) {
          setMessageBody(data.body)
          setGenMsg("AI message draft generated. Review and edit before sending.")
        } else {
          setError("AI did not return a usable message draft")
        }
      } else {
        setError(res.data?.data || res.data?.message || "Could not generate AI message")
      }
    } catch (e) {
      setError("Network error while generating AI message")
    } finally {
      setAiMessageLoading(false)
    }
  }

  const videos = application?.videos || []

  const formatStatus = applicationStatusLabel

  const assessment = application?.assessmentScores || application?.aiAnalysis || {}
  const malpractice = application?.malpracticeCheck
  const scoringStatus = (application?.aiAnalysisStatus || "PENDING").toString().toUpperCase()
  const scoringInProgress = scoringStatus === "IN_PROGRESS" || scoringStatus === "PENDING"
  const interviewAvailable =
    application?.interviewAvailable === true || (application?.videos || []).length > 0
  const cvNeedsReupload = application?.cvNeedsReupload === true
  const cvAvailable = application?.cvAvailable === true && Boolean(application?.cvUrl)
  const numericFinal =
    typeof assessment.finalScore === "number"
      ? assessment.finalScore
      : typeof application?.totalScore === "number"
      ? application.totalScore
      : typeof application?.interviewScore === "number"
      ? application.interviewScore
      : typeof application?.matchingScore === "number"
      ? application.matchingScore
      : null
  const finalScoreValue = scoringInProgress && numericFinal == null ? null : Math.max(0, numericFinal ?? 0)

  const scoreCards = [
    {
      label: "Content",
      value: interviewAvailable ? assessment.contentScore : null,
      unavailable: interviewAvailable ? null : "Interview not available"
    },
    {
      label: "English",
      value: interviewAvailable ? assessment.englishProficiencyScore : null,
      unavailable: interviewAvailable ? null : "Interview not available"
    },
    {
      label: "Confidence",
      value: interviewAvailable ? assessment.confidenceScore : null,
      unavailable: interviewAvailable ? null : "Interview not available"
    },
    {
      label: "CV match",
      value: cvAvailable ? (assessment.cvMatchScore ?? application?.matchingScore) : null,
      unavailable: cvNeedsReupload
        ? "This job seeker should re-upload their CV."
        : cvAvailable
        ? null
        : "CV not available"
    },
    {
      label: "Final",
      value: finalScoreValue,
      unavailable: null
    }
  ]

  return (
    <div className="mx-auto max-w-5xl">
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 sm:py-5">
          <h2 className="text-lg font-semibold leading-snug text-gray-900 sm:text-xl">
            Applicant details
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-gray-600">
            Review this candidate’s CV, video responses, and scores.
          </p>
        </div>
        <div className="p-4 sm:p-6">
          {loading && !application ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
              {error}
            </div>
          ) : !application ? (
            <div className="text-sm text-gray-500">Application not found</div>
          ) : (
            <>
              {genMsg ? (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm font-medium text-green-700">
                  {genMsg}
                </div>
              ) : null}

              <div className="mb-6 grid gap-4 lg:grid-cols-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Candidate
                  </h3>
                  <p className="mt-2 break-words text-sm font-medium text-gray-900">
                    {application.name || application.candidateName || "Candidate"}
                  </p>
                  <p className="mt-1 break-words text-xs text-gray-600">
                    {application.email || "Email not provided"}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Applied: {formatDate(application.createdAt)}
                  </p>
                  {application.cvNeedsReupload ? (
                    <p className="mt-3 text-xs text-amber-700">
                      This job seeker should re-upload their CV.
                    </p>
                  ) : application.cvUrl ? (
                    <a
                      href={application.cvUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-blue-600 hover:underline"
                    >
                      View CV
                    </a>
                  ) : (
                    <p className="mt-3 text-xs text-gray-500">CV not available</p>
                  )}
                  {Array.isArray(application.documents) && application.documents.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-gray-900">Submitted documents</p>
                      <ul className="mt-2 space-y-1">
                        {application.documents.map((doc, idx) => (
                          <li key={`${doc.label}-${idx}`}>
                            {doc.url ? (
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-medium text-blue-600 hover:underline"
                              >
                                {doc.label || doc.fileName || 'Document'}
                              </a>
                            ) : (
                              <span className="text-xs text-gray-600">{doc.label || 'Document'}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 lg:col-span-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Assessment scores
                  </h3>
                  {scoringInProgress && interviewAvailable && numericFinal == null && (
                    <p className="mt-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-sky-900">
                      Auto-scoring in progress. Transcripts are processed first, then scores and analysis appear here automatically.
                    </p>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                    {scoreCards.map((card) => {
                      const display = card.unavailable
                        ? card.unavailable
                        : typeof card.value === "number"
                        ? card.value
                        : scoringInProgress
                        ? <LoadingScoreValue />
                        : card.label === "CV match"
                        ? "CV not available"
                        : 0
                      const isText = typeof display === "string"
                      return (
                        <div key={card.label} className="rounded-md border border-gray-200 bg-white p-2 text-center">
                          <p className="whitespace-nowrap text-[10px] uppercase tracking-wide text-gray-500">{card.label}</p>
                          <p className={`mt-1 font-bold text-gray-900 ${isText ? "text-[10px] leading-tight" : "text-lg"}`}>
                            {display}
                          </p>
                        </div>
                      )
                    })}
                  </div>

                  {malpractice?.flagged && (
                    <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3">
                      <p className="text-xs font-semibold text-amber-900">
                        Malpractice flag ({malpractice.severity || "LOW"})
                      </p>
                      {Array.isArray(malpractice.reasons) && malpractice.reasons.length > 0 && (
                        <ul className="mt-1 list-disc pl-4 text-[11px] text-amber-900">
                          {malpractice.reasons.slice(0, 4).map((reason, idx) => (
                            <li key={idx}>{reason}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-gray-700">
                    <div className="rounded border border-gray-200 bg-white px-2 py-1">
                      Matching:{" "}
                      <span className="font-semibold">
                        {cvAvailable
                          ? (application.matchingScore ?? assessment.cvMatchScore ?? (scoringInProgress ? <LoadingScoreValue /> : "CV not available"))
                          : cvNeedsReupload
                          ? "Re-upload required"
                          : "CV not available"}
                      </span>
                    </div>
                    <div className="rounded border border-gray-200 bg-white px-2 py-1">
                      Interview:{" "}
                      <span className="font-semibold">
                        {interviewAvailable
                          ? (application.interviewScore ?? (scoringInProgress ? "…" : 0))
                          : "Interview not available"}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-1">
                      <label className="mb-1 block text-xs font-medium text-gray-900">
                        Score (0–100)
                      </label>
                      <input
                        value={score}
                        onChange={(e) => setScore(e.target.value)}
                        type="number"
                        min="0"
                        max="100"
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-900">
                        Notes (optional)
                      </label>
                      <input
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        type="text"
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="Reasoning, strengths, concerns..."
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleScore}
                      disabled={scoring}
                      className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                      {scoring ? "Saving..." : "Save score"}
                    </button>
                  </div>

                  {application?.aiAnalysis && (
                    <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
                      <h4 className="text-xs font-semibold text-gray-900">AI Interview Analysis</h4>
                      <p className="mt-1 text-[11px] text-gray-700 whitespace-pre-line">
                        {application.aiAnalysis.summary || "Summary not available."}
                      </p>
                      {typeof application.aiAnalysis.finalScore === "number" && (
                        <p className="mt-2 text-[11px] text-gray-800">
                          Final score: <span className="font-semibold">{application.aiAnalysis.finalScore}</span> / 100
                        </p>
                      )}
                      {typeof application.aiAnalysis.score === "number" && !application.aiAnalysis.finalScore && (
                        <p className="mt-2 text-[11px] text-gray-800">
                          Score: <span className="font-semibold">{application.aiAnalysis.score}</span> / 100
                        </p>
                      )}
                      {Array.isArray(application.aiAnalysis.strengths) && application.aiAnalysis.strengths.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[11px] font-semibold text-gray-900">Strengths</p>
                          <ul className="mt-1 list-disc pl-4 text-[11px] text-gray-700">
                            {application.aiAnalysis.strengths.slice(0, 6).map((s, idx) => (
                              <li key={idx}>{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {Array.isArray(application.aiAnalysis.weaknesses) && application.aiAnalysis.weaknesses.length > 0 && (
                        <div className="mt-2">
                          <p className="text-[11px] font-semibold text-gray-900">Weaknesses</p>
                          <ul className="mt-1 list-disc pl-4 text-[11px] text-gray-700">
                            {application.aiAnalysis.weaknesses.slice(0, 6).map((w, idx) => (
                              <li key={idx}>{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {application?.transportReport &&
                    (application.transportReport.employerReport ||
                      application.transportReport.distanceKm != null) && (
                    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                      <h4 className="text-xs font-semibold text-gray-900">Commute &amp; transport</h4>
                      {(application.transportReport.distanceKm != null ||
                        application.transportReport.estimatedTransportCostNgN != null) && (
                        <dl className="mt-2 grid gap-2 text-[11px] text-gray-700 sm:grid-cols-3">
                          {application.transportReport.distanceKm != null && (
                            <div>
                              <dt className="font-semibold text-gray-500">One-way distance</dt>
                              <dd>{Number(application.transportReport.distanceKm).toFixed(1)} km</dd>
                            </div>
                          )}
                          {application.transportReport.estimatedTransportCostNgN != null && (
                            <div>
                              <dt className="font-semibold text-gray-500">Est. monthly transport</dt>
                              <dd>
                                {(() => {
                                  const ccy = application.transportReport.transportCurrency || "NGN"
                                  const amt = Number(application.transportReport.estimatedTransportCostNgN)
                                  if (!["NGN", "USD", "EUR"].includes(ccy)) {
                                    return `${amt.toLocaleString()} ${ccy}`
                                  }
                                  try {
                                    return new Intl.NumberFormat("en-US", {
                                      style: "currency",
                                      currency: ccy,
                                      maximumFractionDigits: 0
                                    }).format(amt)
                                  } catch {
                                    return `${amt.toLocaleString()} ${ccy}`
                                  }
                                })()}
                              </dd>
                            </div>
                          )}
                          {typeof application.transportReport.viable === "boolean" && (
                            <div>
                              <dt className="font-semibold text-gray-500">Vs salary band (heuristic)</dt>
                              <dd>
                                {application.transportReport.viable
                                  ? "Favorable"
                                  : "Tight / review"}
                              </dd>
                            </div>
                          )}
                        </dl>
                      )}
                      {application.transportReport.employerReport && (
                        <p className="mt-2 text-[11px] text-gray-700 whitespace-pre-line">
                          {application.transportReport.employerReport}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 lg:col-span-1">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Application status & last message to candidate
                  </h3>
                  <p className="mt-2 text-xs text-gray-600">
                    Current stage of this application and the latest message the candidate will see in their dashboard.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                      {formatStatus(application.status)}
                    </span>
                    <span className="text-[10px] uppercase text-gray-500">
                      Last updated {formatDate(application.updatedAt)}
                    </span>
                  </div>
                  {application.resultMessage && (
                    <div className="mt-3 rounded-md bg-white p-3 text-xs text-gray-800 whitespace-pre-line border border-gray-200">
                      {application.resultMessage}
                    </div>
                  )}
                  {Array.isArray(application.employerNotes) && application.employerNotes.length > 0 && (
                    <div className="mt-3">
                      <button
                        type="button"
                        onClick={() => setNotesExpanded(!notesExpanded)}
                        className="text-[11px] font-medium text-slate-700 hover:text-slate-900 underline"
                      >
                        {notesExpanded
                          ? "Hide internal notes"
                          : `View internal notes (${application.employerNotes.length})`}
                      </button>
                      {notesExpanded && (
                        <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto text-[11px] text-slate-600">
                          {application.employerNotes
                            .slice()
                            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                            .map((note, idx) => (
                              <li key={idx} className="border border-slate-200 bg-white rounded px-2 py-1">
                                <div className="flex justify-between gap-2">
                                  <span className="font-semibold">Note</span>
                                  {note.createdAt && (
                                    <span className="text-[10px] text-slate-400">
                                      {formatDate(note.createdAt)}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 whitespace-pre-line">
                                  {note.note}
                                </div>
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6 rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-200 px-4 py-3 sm:px-5 sm:py-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Video interview
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    One question per video. Videos may expire after 30 days based on your
                    settings.
                  </p>
                </div>
                {videos.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">
                    This candidate has not completed the video interview yet.
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {videos.map((v, idx) => (
                      <li key={v._id || idx} className="p-4 sm:p-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">
                              Question {idx + 1}
                            </p>
                            <p className="mt-1 text-xs text-gray-600">
                              {v.question || "Question text not available"}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              Recorded: {formatDate(v.createdAt)}
                            </p>
                          </div>
                          {v.url ? (
                            <div className="w-full sm:w-56">
                              <video
                                controls
                                className="w-full rounded-lg border border-gray-200 bg-black"
                              >
                                <source src={v.url} />
                              </video>
                            </div>
                          ) : (
                            <div className="w-full sm:w-56 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500 text-center">
                              Video URL not available.
                            </div>
                          )}
                        </div>
                        <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 p-2">
                          <p className="text-[11px] font-semibold text-gray-800">Transcription</p>
                          <p className="mt-1 whitespace-pre-line text-[11px] text-gray-700">
                            {v.transcriptText || "No transcript available yet."}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-200 px-4 py-3 sm:px-5 sm:py-4">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Message candidate
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Send a generic update or use accept/reject to trigger templates and email.
                  </p>
                </div>
                <div className="p-4 sm:p-5 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setMessageType("GENERIC")}
                      className={`rounded-full px-3 py-1 text-xs font-medium border ${
                        messageType === "GENERIC"
                          ? "border-black bg-black text-white"
                          : "border-gray-300 bg-white text-gray-700"
                      }`}
                    >
                      Generic message
                    </button>
                    <button
                      type="button"
                      onClick={() => setMessageType("ACCEPT")}
                      className={`rounded-full px-3 py-1 text-xs font-medium border ${
                        messageType === "ACCEPT"
                          ? "border-green-700 bg-green-700 text-white"
                          : "border-gray-300 bg-white text-gray-700"
                      }`}
                    >
                      Accept candidate
                    </button>
                    <button
                      type="button"
                      onClick={() => setMessageType("REJECT")}
                      className={`rounded-full px-3 py-1 text-xs font-medium border ${
                        messageType === "REJECT"
                          ? "border-red-700 bg-red-700 text-white"
                          : "border-gray-300 bg-white text-gray-700"
                      }`}
                    >
                      Reject candidate
                    </button>
                  </div>
                  {(messageType === "ACCEPT" || messageType === "REJECT") && (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[11px] text-gray-600">
                        Tip: Load the template, then personalize it.
                      </p>
                      <button
                        type="button"
                        onClick={handleLoadTemplate}
                        disabled={templateLoading}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {templateLoading ? "Loading..." : "Load template"}
                      </button>
                    </div>
                  )}
                  <textarea
                    value={messageBody}
                    onChange={(e) => setMessageBody(e.target.value)}
                    rows={3}
                    className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder={
                      messageType === "ACCEPT"
                        ? "Optional: personalize your acceptance message or leave blank to use the default template."
                        : messageType === "REJECT"
                        ? "Optional: personalize your rejection message or leave blank to use the default template."
                        : "e.g. Thank you for applying. We’d like to invite you to the next stage..."
                    }
                  />
                  {messageType === "REJECT" && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-900">
                        Optional short advice to candidate
                      </label>
                      <input
                        value={advice}
                        onChange={(e) => setAdvice(e.target.value)}
                        type="text"
                        className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder="e.g. Improve your communication skills, gain more Excel experience..."
                      />
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={handleAiMessage}
                      disabled={aiMessageLoading || messageType === "GENERIC"}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {aiMessageLoading ? "Asking AI..." : "AI message draft"}
                    </button>
                    <button
                      type="button"
                      onClick={handleMessage}
                      disabled={messaging}
                      className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                      {messaging
                        ? "Sending..."
                        : messageType === "ACCEPT"
                        ? "Send accept & notify"
                        : messageType === "REJECT"
                        ? "Send reject & notify"
                        : "Send message"}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EmployerApplicantDetails

